import type { Router } from 'vue-router'
import { ApiError } from '@/types/api'

/* ============================================================
 * 支付渠道错误文案（后端设计文档 §2.8 错误表 + §15）
 *
 * 支付配置纯表驱动（`pay_channel_config`）：
 *   - 渠道无可用商户 → 「该支付渠道未配置可用商户」
 *   - paymentMethod 非 wechat/alipay（如 balance 预留值）→ 「暂不支持该支付方式」
 * 两条文案课程订单（§2.8）与积分充值（§13.8）共用。
 * ============================================================ */

/** 渠道未配置商户（下单前置校验失败，用户侧不可自愈 → 引导联系客服） */
export const POINTS_CHANNEL_NOT_CONFIGURED_MSG = '该支付渠道未配置可用商户'

/** 支付方式不支持（如传了 balance 预留渠道） */
export const PAYMENT_NOT_SUPPORTED_MSG = '暂不支持该支付方式'

/** 后端文案中「渠道未配置商户」的判定关键片段 */
const CHANNEL_NOT_CONFIGURED_TOKEN = '未配置可用商户'

/** 后端文案中「支付方式不支持」的判定关键片段 */
const PAYMENT_NOT_SUPPORTED_TOKEN = '不支持该支付方式'

/** 提取任意错误的可读 msg（trim 后）；无 msg 返回空串。 */
function rawMessageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message?.trim() ?? ''
  if (error instanceof Error) return error.message?.trim() ?? ''
  return ''
}

/**
 * 是否为「该支付渠道未配置可用商户」。
 * 后端文案可能带模块前缀，故用关键片段包含匹配（ApiError / Error 均生效）。
 */
export function isPaymentChannelNotConfigured(err: unknown): boolean {
  return rawMessageOf(err).includes(CHANNEL_NOT_CONFIGURED_TOKEN)
}

/** 是否为「暂不支持该支付方式」。同上，关键片段包含匹配。 */
export function isPaymentNotSupported(err: unknown): boolean {
  return rawMessageOf(err).includes(PAYMENT_NOT_SUPPORTED_TOKEN)
}

export function getErrorMessage(error: unknown, fallback = '操作失败，请稍后重试'): string {
  if (isPaymentChannelNotConfigured(error)) return POINTS_CHANNEL_NOT_CONFIGURED_MSG
  if (isPaymentNotSupported(error)) return PAYMENT_NOT_SUPPORTED_MSG
  if (error instanceof ApiError) return error.message
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export const ErrorCode = {
  Success: 200,
  Unauthenticated: 401,
  Forbidden: 403,
  BusinessError: 500,
  LessonNotFound: 50001,
  OrderNotFound: 50002,
  OrderPaid: 50003,
  CourseOffShelf: 50004,
  VodNotReady: 50005,
} as const

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode]

export type ErrorCodeAction =
  { type: 'redirect'; path: string } | { type: 'toast'; message: string } | { type: 'none' }

const COURSE_DETAIL_PATTERN = /^\/app\/course\/(\d+)(?:\/|$)/

export function extractCourseId(url: string | undefined): number | null {
  if (!url) return null
  const match = COURSE_DETAIL_PATTERN.exec(url)
  if (!match) return null
  const id = Number.parseInt(match[1], 10)
  return Number.isFinite(id) ? id : null
}

export function getErrorCodeAction(code: number): ErrorCodeAction {
  switch (code) {
    case ErrorCode.LessonNotFound:
    case ErrorCode.OrderNotFound:
    case ErrorCode.OrderPaid:
      return { type: 'redirect', path: '' }
    case ErrorCode.CourseOffShelf:
      return { type: 'redirect', path: '/' }
    case ErrorCode.VodNotReady:
      return { type: 'toast', message: '视频尚未转码完成，请稍后再试或联系讲师' }
    default:
      return { type: 'none' }
  }
}

export function handleApiError(error: unknown, router: Router): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.code === ErrorCode.Unauthenticated) return false
  if (error.code === ErrorCode.Forbidden) return false
  if (error.code === ErrorCode.BusinessError) return false

  const action = getErrorCodeAction(error.code)
  if (action.type === 'none') return false

  if (action.type === 'redirect') {
    let target = action.path
    if (target === '') {
      const courseId = extractCourseId(error.url)
      target = courseId ? `/courses/${courseId}` : '/'
    }
    void router.push(target)
    return true
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('xuntu:error-toast', { detail: { message: action.message } }),
    )
  }
  return true
}

/* ============================================================
 * 工单（服务订单）模块错误文案兜底（§6.8）
 *
 * 后端订单接口统一返回 code=500 + msg 文案；前端 ApiError.message
 * 已是后端文案，无需 fallback。但「钱包不足」这类需要前端特殊处理
 * 的文案，统一集中在此，方便以后扩展 / i18n。
 *
 * 业务约束（AGENTS.md + 03-state-machines.md §1 + 07-test-and-acceptance.md §1）：
 *   - 严禁「待支付 / 已支付 / 退款 / 申请退款」等支付文案（ORDER-010）
 *   - 钱包不足时不补缴，弹"请联系客服"
 * ============================================================ */

/** 钱包不足 → 弹"请联系客服"，**不补缴**（§6.8 + AGENTS.md 常见踩坑） */
export const ORDER_WALLET_INSUFFICIENT_MSG = '企业钱包余额不足，请联系客服'

/**
 * 工单错误通用提取：ApiError.message 已带后端文案；只有 msg 为空 / 非 ApiError 时
 * 才走 fallback。**当前工单全部走 ApiError.message**，本工具主要防御性使用。
 */
export function getOrderErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = error.message?.trim()
    if (msg) return msg
  }
  if (error instanceof Error && error.message) return error.message
  return '操作失败，请稍后重试'
}

/**
 * 判断是否为「钱包不足」错误（用于 DetailView 验收按钮的特殊分支）。
 * 后端文案固定为「企业钱包余额不足，请联系客服」，做精确匹配。
 */
export function isOrderWalletInsufficient(error: unknown): boolean {
  return getOrderErrorMessage(error).includes('钱包') &&
    getOrderErrorMessage(error).includes('请联系客服')
}

/* ============================================================
 * 工单错误集中处理（§10.4 P1 #22）
 *
 * 后端工单接口统一返回 code=500 + msg 文案（§6.8 / §11.7）；
 * 此模块提供：
 *   1. ORDER_ERROR_FALLBACK：6 类操作的 fallback 文案（按操作分发）
 *   2. 3 个常见检测：forbidden / auth-required / already-applied
 *   3. handleOrderApiError：统一处理器
 *      - 钱包不足 → toast 不跳转
 *      - 状态非法 → toast + 跳 /orders/{orderNo}
 *      - 无权/未认证/已申请 → toast 不跳转
 *      - 其它 → 返回 false（让调用方走默认 toast）
 * ============================================================ */

/** 6 类工单操作的 fallback 文案（按 `OrderOperation` 枚举使用） */
export const ORDER_ERROR_FALLBACK = {
  cancel: '当前状态不可取消',
  apply: '当前订单不在接单池',
  submitProgress: '仅接单创作者可提交交付',
  rejectDelivery: '当前状态不可驳回',
  accept: '当前状态不可验收',
  audit: '当前状态不可审核',
  markHot: '无效的热门标记',
} as const

export type OrderOperation = keyof typeof ORDER_ERROR_FALLBACK

/** "无权操作该订单" — 出现在 cancel / assign / progress 等非发单方调用时 */
export const ORDER_ERROR_FORBIDDEN_MSG = '无权操作该订单'

/** "接单需先完成创作者认证" — apply 时未达 creatorStatus=3 */
export const ORDER_ERROR_AUTH_REQUIRED_MSG = '接单需先完成创作者认证'

/** "您已申请过该订单" — apply 重复提交 */
export const ORDER_ERROR_ALREADY_APPLIED_MSG = '您已申请过该订单'

/** 当前 error 的 msg（trim 后）。非 ApiError 返回空串。 */
function msgOf(error: unknown): string {
  return getOrderErrorMessage(error).trim()
}

/** 检测「状态非法流转」文案。`op` 必须能精确匹配 ORDER_ERROR_FALLBACK[op]。仅对 ApiError 生效。 */
export function isOrderStatusInvalid(error: unknown, op: OrderOperation): boolean {
  if (!(error instanceof ApiError)) return false
  return msgOf(error) === ORDER_ERROR_FALLBACK[op]
}

/** 检测「无权操作该订单」。仅对 ApiError 生效。 */
export function isOrderForbidden(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return msgOf(error) === ORDER_ERROR_FORBIDDEN_MSG
}

/** 检测「接单需先完成创作者认证」。仅对 ApiError 生效。 */
export function isOrderAuthRequired(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return msgOf(error) === ORDER_ERROR_AUTH_REQUIRED_MSG
}

/** 检测「您已申请过该订单」。仅对 ApiError 生效。 */
export function isOrderAlreadyApplied(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return msgOf(error) === ORDER_ERROR_ALREADY_APPLIED_MSG
}

/** 派发错误 toast 事件（与 handleApiError 同源，保持单一来源） */
function dispatchErrorToast(message: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('xuntu:error-toast', { detail: { message } }),
  )
}

export interface HandleOrderApiErrorOptions {
  /** 订单号，用于「状态非法流转」时跳详情页 `/orders/{orderNo}`。不传则不跳转（仅 toast）。 */
  orderNo?: string
}

/**
 * 工单错误统一处理器（§10.4 P1 #22）。返回 true 表示已处理（已 toast 或已跳转）。
 *
 * 处理顺序：
 *   1. 钱包不足（`isOrderWalletInsufficient`）→ toast ORDER_WALLET_INSUFFICIENT_MSG
 *   2. 状态非法（遍历 ORDER_ERROR_FALLBACK）→ toast fallback + router.push(/orders/{orderNo})
 *   3. 无权 / 未认证 / 已申请 → toast 对应文案
 *   4. 其它 → 返回 false（调用方走默认 toast）
 */
export function handleOrderApiError(
  error: unknown,
  router: Router,
  options: HandleOrderApiErrorOptions = {},
): boolean {
  if (!(error instanceof ApiError)) return false

  if (isOrderWalletInsufficient(error)) {
    dispatchErrorToast(ORDER_WALLET_INSUFFICIENT_MSG)
    return true
  }

  for (const op of Object.keys(ORDER_ERROR_FALLBACK) as OrderOperation[]) {
    if (isOrderStatusInvalid(error, op)) {
      dispatchErrorToast(ORDER_ERROR_FALLBACK[op])
      if (options.orderNo) {
        void router.push(`/orders/${options.orderNo}`)
      }
      return true
    }
  }

  if (isOrderForbidden(error)) {
    dispatchErrorToast(ORDER_ERROR_FORBIDDEN_MSG)
    return true
  }
  if (isOrderAuthRequired(error)) {
    dispatchErrorToast(ORDER_ERROR_AUTH_REQUIRED_MSG)
    return true
  }
  if (isOrderAlreadyApplied(error)) {
    dispatchErrorToast(ORDER_ERROR_ALREADY_APPLIED_MSG)
    return true
  }

  return false
}

/* ============================================================
 * 支付错误集中处理（§2.8 错误表 + §13.8）
 *
 * 课程订单（§2.8）与积分充值（§13.8）共用同一套支付错误检测：
 *   - 渠道未配置商户 / 支付方式不支持（Wave 1 已加的 2 个工具）
 *   - 钱包不足（复用 §6.8 工单文案检测；支付侧仅兜底）
 *   - 状态非法流转（复用 ORDER_ERROR_FALLBACK 文案集合）
 * 默认 toast 走 `xuntu:error-toast` 事件，与 handleApiError / handleOrderApiError
 * 同源，保持单一来源。
 * ============================================================ */

export interface PaymentErrorOptions {
  /** 渠道未配置可用商户 → 引导联系客服 */
  onChannelNotConfigured?: () => void
  /** 支付方式不支持（如传了 balance 预留渠道） */
  onNotSupported?: () => void
  /** 工单钱包不足（复用 §6.8） */
  onWalletInsufficient?: () => void
  /** 状态非法流转（命中 ORDER_ERROR_FALLBACK 任一文案） */
  onStatusInvalid?: () => void
  /** 默认 toast（调用方覆盖默认事件派发时使用） */
  defaultToast?: (msg: string) => void
}

/**
 * 支付错误统一处理器。返回 true 表示已处理（已回调或已派发 toast）。
 *
 * 检测顺序：
 *   1. 渠道未配置 → onChannelNotConfigured
 *   2. 支付方式不支持 → onNotSupported
 *   3. 钱包不足 → onWalletInsufficient
 *   4. 状态非法（遍历 ORDER_ERROR_FALLBACK）→ onStatusInvalid
 *   5. 其它 → 返回 false（调用方走默认 toast）
 *
 * 状态非法分支仅对 ApiError 生效；其余分支对 ApiError / Error 均生效。
 * 4 类回调未传时静默走 `defaultToast`（未传则派发 `xuntu:error-toast` 事件）。
 * 调用方若希望无回调时仍弹兜底 toast，可传入 `defaultToast` 覆盖。
 */
export function handlePaymentApiError(
  error: unknown,
  options: PaymentErrorOptions = {},
): boolean {
  const {
    onChannelNotConfigured,
    onNotSupported,
    onWalletInsufficient,
    onStatusInvalid,
    defaultToast,
  } = options

  if (isPaymentChannelNotConfigured(error)) {
    if (onChannelNotConfigured) onChannelNotConfigured()
    else if (defaultToast) defaultToast(POINTS_CHANNEL_NOT_CONFIGURED_MSG)
    else dispatchErrorToast(POINTS_CHANNEL_NOT_CONFIGURED_MSG)
    return true
  }
  if (isPaymentNotSupported(error)) {
    if (onNotSupported) onNotSupported()
    else if (defaultToast) defaultToast(PAYMENT_NOT_SUPPORTED_MSG)
    else dispatchErrorToast(PAYMENT_NOT_SUPPORTED_MSG)
    return true
  }
  if (isOrderWalletInsufficient(error)) {
    if (onWalletInsufficient) onWalletInsufficient()
    else if (defaultToast) defaultToast(ORDER_WALLET_INSUFFICIENT_MSG)
    else dispatchErrorToast(ORDER_WALLET_INSUFFICIENT_MSG)
    return true
  }
  if (error instanceof ApiError) {
    for (const op of Object.keys(ORDER_ERROR_FALLBACK) as OrderOperation[]) {
      if (isOrderStatusInvalid(error, op)) {
        if (onStatusInvalid) onStatusInvalid()
        else if (defaultToast) defaultToast(ORDER_ERROR_FALLBACK[op])
        else dispatchErrorToast(ORDER_ERROR_FALLBACK[op])
        return true
      }
    }
  }
  return false
}
